import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Lock, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, API, FILES_BASE, errMsg, dt } from "@/lib/api";
import ReportRenderer from "@/components/ReportRenderer";

const DEFAULT_PRIMARY = "#0B5D36";
const fileUrl = (u) => (u && u.startsWith("/api/") ? FILES_BASE + u : u);

export default function PublicShare() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState(null);
  const [pw, setPw] = useState("");

  const open = async (password) => {
    try { setView((await api.post(`/public/share/${token}/view`, { password: password || null })).data); } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => {
    api.get(`/public/share/${token}`).then((r) => { setMeta(r.data); if (!r.data.requires_password) open(); }).catch((e) => setError(errMsg(e)));
  }, [token]); // eslint-disable-line

  const pdf = () => { window.location.href = `${API}/public/share/${token}/pdf?key=${encodeURIComponent(view.view_key)}`; };

  const brand = (view || meta)?.branding || {};
  const primary = brand.primary_color || DEFAULT_PRIMARY;
  const orgName = view?.organization_name || meta?.organization_name || "";
  const logo = fileUrl(brand.logo_url) || "/logo.webp";

  return (
    <div className="min-h-screen bg-[#F6F7F9] text-slate-100" data-testid="public-share-page">
      <div className="h-1.5 w-full" style={{ background: primary }} data-testid="public-share-brandbar" />
      <header className="bg-white border-b border-slate-800 px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <img src={logo} alt={orgName || "LaunchX360"} className="h-10 max-w-[180px] object-contain" data-testid="public-share-logo" />
          {brand.logo_url && orgName && <span className="text-sm font-semibold text-slate-200 hidden sm:inline truncate" data-testid="public-share-org-name">{orgName}</span>}
        </div>
        <span className="text-xs font-mono text-slate-500 flex items-center gap-1 shrink-0"><Eye className="h-3.5 w-3.5" />View-only shared report</span>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {error && <div className="lx-card text-center py-16" data-testid="public-share-error"><div className="text-lg font-bold">Link unavailable</div><div className="text-sm text-slate-500 mt-2">{error}</div></div>}
        {meta && !view && meta.requires_password && (
          <form className="lx-card max-w-md mx-auto space-y-4 py-8" onSubmit={(e) => { e.preventDefault(); open(pw); }} data-testid="public-share-password-form">
            <Lock className="h-6 w-6" style={{ color: primary }} />
            <div><div className="font-bold text-lg">{meta.title}</div><div className="text-sm text-slate-500">{meta.organization_name ? `Shared by ${meta.organization_name}. ` : ""}Enter the password to view.</div></div>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" data-testid="public-share-password-input" />
            <Button className="w-full" style={{ backgroundColor: primary }} data-testid="public-share-unlock-button">Unlock report</Button>
          </form>
        )}
        {view && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-widest mb-2" style={{ color: primary }} data-testid="public-share-brand-label">{view.organization_name ? `Prepared by ${view.organization_name}` : "Shared report"}</div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight" data-testid="public-share-title">{view.title}</h1>
                <p className="text-xs text-slate-500 mt-2 font-mono">Shared {dt(view.created_at)} · expires {dt(view.expiry_date)}</p>
              </div>
              <Button onClick={pdf} style={{ backgroundColor: primary }} data-testid="public-share-pdf-button"><Download className="h-4 w-4 mr-2" />Download PDF</Button>
            </div>
            <ReportRenderer data={view.data} />
            {view.footer && Object.keys(view.footer).length > 0 && (
              <div className="lx-card text-xs text-slate-500 space-y-1" data-testid="public-share-footer">
                {[view.footer.company_name, view.footer.phone, view.footer.email, view.footer.website].filter(Boolean).join(" · ")}
                {view.footer.address && <div>{view.footer.address}</div>}
                {view.footer.rera && <div>RERA: {view.footer.rera}</div>}
                {view.footer.disclaimer && <div>{view.footer.disclaimer}</div>}
              </div>
            )}
          </div>
        )}
      </main>
      <footer className="text-center text-[11px] font-mono text-slate-400 pb-8">Powered by LaunchX360 · Plan | Market | Launch | Sell | Grow · <a href="/privacy-policy" className="hover:underline" data-testid="share-privacy-link">Privacy Policy</a></footer>
    </div>
  );
}
